import React, { useState, useRef } from 'react';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@frontend/shadcn/components/ui/toggle-group';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Avatar, AvatarImage } from '@frontend/shadcn/components/ui/avatar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTags,
  faImage,
  faRocket,
  faBullhorn,
  faGift,
  faShoppingCart,
  faLightbulb,
  faStar,
  faChartLine,
  faUsers,
  faTag,
  faBriefcase,
  faMoneyBill,
  faPercent,
  faDollarSign,
  faShieldHalved,
  faGlobe,
  faBank,
  faWallet,
  faHeart,
  faLock,
  faFire,
  faHandshake,
  faMusic,
  faSpa,
  faSeedling,
  faLink,
  faFile,
  faPenNib,
  faBook,
  faEnvelope,
  faFolder,
  faPaperclip,
  faFeather,
  faBullseye,
  faHashtag,
  faUmbrella,
  faCloud,
  faSnowflake,
  faSun,
  faEarthAmericas,
  faPaperPlane,
  faFlag,
  faCompass,
  faChartSimple,
  faMedal,
  faUser,
  faCircleInfo,
  faBell,
  faPhotoFilm,
  faPaintbrush,
  faTicket,
  faReceipt,
  faSackDollar,
  faPiggyBank,
  faCartShopping,
  faBagShopping,
  faShop,
  faHandHoldingHeart,
  faPersonBreastfeeding,
  faBed,
  faHouse,
  faBoxArchive,
  faScaleUnbalanced,
  faCalendar,
  faClock,
  faHourglassHalf,
  faCamera,
  faVideo,
  faNotesMedical,
  faStarOfLife,
  faThumbsUp,
  faCreditCard,
  faPodcast,
  faSatelliteDish,
  faCube,
  faPlane,
  faWarehouse,
  faShirt,
  faPuzzlePiece,
  faChessKnight,
  faCheck,
  faXmark,
  faFishFins,
  faUtensils,
  faMugHot,
  faCookieBite,
  faCakeCandles,
  faLaptop,
  faGlasses,
  faBath,
} from '@fortawesome/free-solid-svg-icons';
import { XmarkIcon, PlusIcon } from '@revfluence/fresh-icons/regular/esm';
import { Search } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@frontend/shadcn/components/ui/hover-card';

// Define icon options
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export interface IconOption {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  name: string;
  keywords: string[];
}

// Expanded list of icons for the campaign use case
export const iconOptions: IconOption[] = [
  { icon: faBriefcase, name: 'briefcase', keywords: ['work', 'business', 'job'] },
  { icon: faTag, name: 'tag', keywords: ['label', 'price', 'discount'] },
  { icon: faTags, name: 'tags', keywords: ['labels', 'price', 'discount'] },
  { icon: faMoneyBill, name: 'money-bill', keywords: ['cash', 'payment', 'currency'] },
  { icon: faPercent, name: 'percent', keywords: ['discount', 'sale', 'offer'] },
  { icon: faDollarSign, name: 'dollar-sign', keywords: ['money', 'price', 'payment'] },
  { icon: faShieldHalved, name: 'shield-halved', keywords: ['security', 'protection'] },
  { icon: faGlobe, name: 'globe', keywords: ['world', 'international', 'web'] },
  { icon: faBank, name: 'bank', keywords: ['bank', 'institution', 'building'] },
  { icon: faWallet, name: 'wallet', keywords: ['money', 'cash', 'payment'] },
  { icon: faHeart, name: 'heart', keywords: ['love', 'like', 'favorite'] },
  { icon: faGift, name: 'gift', keywords: ['present', 'reward', 'package'] },
  { icon: faLock, name: 'lock', keywords: ['security', 'private', 'protected'] },
  { icon: faFire, name: 'fire', keywords: ['hot', 'trending', 'popular'] },
  { icon: faShoppingCart, name: 'shopping-cart', keywords: ['shop', 'buy', 'purchase'] },
  { icon: faRocket, name: 'rocket', keywords: ['launch', 'space', 'campaign'] },
  { icon: faHandshake, name: 'handshake', keywords: ['agreement', 'deal', 'partnership'] },
  { icon: faStar, name: 'star', keywords: ['rating', 'favorite', 'important'] },
  { icon: faMusic, name: 'music', keywords: ['song', 'audio', 'sound'] },
  { icon: faSpa, name: 'spa', keywords: ['relax', 'beauty', 'treatment'] },
  { icon: faSeedling, name: 'seedling', keywords: ['plant', 'growth', 'nature'] },
  { icon: faLink, name: 'link', keywords: ['url', 'chain', 'connect'] },
  { icon: faFile, name: 'file', keywords: ['document', 'paper', 'new'] },
  { icon: faPenNib, name: 'pen-nib', keywords: ['write', 'edit', 'create'] },
  { icon: faBook, name: 'book', keywords: ['read', 'library', 'education'] },
  { icon: faEnvelope, name: 'envelope', keywords: ['mail', 'email', 'message'] },
  { icon: faFolder, name: 'folder', keywords: ['directory', 'files'] },
  { icon: faPaperclip, name: 'paperclip', keywords: ['attachment', 'file'] },
  { icon: faFeather, name: 'feather', keywords: ['light', 'weight', 'feather'] },
  { icon: faBullseye, name: 'bullseye', keywords: ['target', 'aim', 'hit'] },
  { icon: faHashtag, name: 'hashtag', keywords: ['number', 'pound', 'social'] },
  { icon: faUmbrella, name: 'umbrella', keywords: ['rain', 'protection', 'weather'] },
  { icon: faCloud, name: 'cloud', keywords: ['weather', 'storage', 'sky'] },
  { icon: faSnowflake, name: 'snowflake', keywords: ['winter', 'cold', 'freeze'] },
  { icon: faSun, name: 'sun', keywords: ['weather', 'bright', 'day'] },
  { icon: faEarthAmericas, name: 'earth-americas', keywords: ['world', 'planet', 'globe'] },
  { icon: faPaperPlane, name: 'paper-plane', keywords: ['send', 'message', 'mail'] },
  { icon: faFlag, name: 'flag', keywords: ['report', 'marker', 'notice'] },
  { icon: faCompass, name: 'compass', keywords: ['navigation', 'direction'] },
  { icon: faChartSimple, name: 'chart-simple', keywords: ['statistics', 'analytics'] },
  { icon: faChartLine, name: 'chart-line', keywords: ['statistics', 'analytics', 'growth'] },
  { icon: faMedal, name: 'medal', keywords: ['award', 'achievement', 'honor'] },
  { icon: faUser, name: 'user', keywords: ['person', 'profile', 'account'] },
  { icon: faUsers, name: 'users', keywords: ['team', 'group', 'people'] },
  { icon: faLightbulb, name: 'lightbulb', keywords: ['idea', 'bright', 'creative'] },
  { icon: faBullhorn, name: 'bullhorn', keywords: ['announce', 'marketing', 'promotion'] },
  { icon: faImage, name: 'image', keywords: ['picture', 'photo', 'media'] },
  { icon: faBell, name: 'bell', keywords: ['notification', 'alert', 'reminder'] },
  { icon: faPhotoFilm, name: 'photo-film', keywords: ['photo', 'picture', 'image'] },
  { icon: faPaintbrush, name: 'paint-brush', keywords: ['design', 'art', 'paint'] },
  { icon: faTicket, name: 'ticket', keywords: ['event', 'admission', 'pass'] },
  { icon: faReceipt, name: 'receipt', keywords: ['bill', 'invoice', 'payment'] },
  { icon: faSackDollar, name: 'sack-dollar', keywords: ['money', 'bag', 'savings'] },
  { icon: faPiggyBank, name: 'piggy-bank', keywords: ['savings', 'money', 'bank'] },
  { icon: faCartShopping, name: 'cart-shopping', keywords: ['shopping', 'buy', 'purchase'] },
  { icon: faBagShopping, name: 'bag-shopping', keywords: ['shopping', 'buy', 'purchase'] },
  { icon: faShop, name: 'shop', keywords: ['store', 'retail', 'purchase'] },
  { icon: faHandHoldingHeart, name: 'hand-holding-heart', keywords: ['love', 'care', 'support'] },
  { icon: faPersonBreastfeeding, name: 'person-breastfeeding', keywords: ['feed', 'nurse', 'baby'] },
  { icon: faBed, name: 'bed', keywords: ['sleep', 'hotel', 'furniture'] },
  { icon: faHouse, name: 'house', keywords: ['home', 'building', 'property'] },
  { icon: faBoxArchive, name: 'box-archive', keywords: ['storage', 'package'] },
  { icon: faScaleUnbalanced, name: 'scale-unbalanced', keywords: ['balance', 'weight', 'scale'] },
  { icon: faCalendar, name: 'calendar', keywords: ['date', 'schedule', 'time'] },
  { icon: faClock, name: 'clock', keywords: ['time', 'watch', 'schedule'] },
  { icon: faHourglassHalf, name: 'hourglass-half', keywords: ['time', 'wait', 'loading'] },
  { icon: faCamera, name: 'camera', keywords: ['photo', 'picture', 'capture'] },
  { icon: faVideo, name: 'video', keywords: ['movie', 'film', 'media'] },
  { icon: faNotesMedical, name: 'notes-medical', keywords: ['health', 'medical', 'note'] },
  { icon: faStarOfLife, name: 'star-of-life', keywords: ['death', 'funeral', 'grave'] },
  { icon: faThumbsUp, name: 'thumbs-up', keywords: ['like', 'agree', 'approval'] },
  { icon: faCreditCard, name: 'credit-card', keywords: ['payment', 'card', 'purchase'] },
  { icon: faPodcast, name: 'podcast', keywords: ['audio', 'radio', 'podcast'] },
  { icon: faSatelliteDish, name: 'satellite-dish', keywords: ['satellite', 'communication', 'dish'] },
  { icon: faCube, name: 'cube', keywords: ['box', '3d', 'package'] },
  { icon: faPlane, name: 'plane', keywords: ['travel', 'flight', 'trip'] },
  { icon: faWarehouse, name: 'warehouse', keywords: ['storage', 'inventory'] },
  { icon: faShirt, name: 'shirt', keywords: ['clothing', 'fashion', 'shirt'] },
  { icon: faPuzzlePiece, name: 'puzzle-piece', keywords: ['game', 'addon'] },
  { icon: faChessKnight, name: 'chess-knight', keywords: ['game', 'strategy'] },
  { icon: faCheck, name: 'check', keywords: ['complete', 'done', 'correct'] },
  { icon: faXmark, name: 'xmark', keywords: ['cancel', 'reject', 'deny'] },
  { icon: faFishFins, name: 'fish-fins', keywords: ['fish', 'sea', 'aquatic'] },
  { icon: faUtensils, name: 'utensils', keywords: ['food', 'restaurant', 'eat'] },
  { icon: faMugHot, name: 'mug-hot', keywords: ['coffee', 'drink', 'tea'] },
  { icon: faCookieBite, name: 'cookie-bite', keywords: ['bitten', 'cookie', 'bite'] },
  { icon: faCakeCandles, name: 'cake-candles', keywords: ['birthday', 'celebration', 'dessert'] },
  { icon: faLaptop, name: 'laptop', keywords: ['computer', 'device'] },
  { icon: faGlasses, name: 'glasses', keywords: ['view', 'vision', 'read'] },
  { icon: faBath, name: 'bath', keywords: ['relax', 'bathroom', 'shower'] },
];

interface IconSelectorProps {
  onSelectIcon: (icon: IconOption) => void;
}

// Icon selector component
export const IconSelector: React.FC<IconSelectorProps> = ({ onSelectIcon }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredIcons = React.useMemo(() => {
    if (!searchQuery) return iconOptions;

    const query = searchQuery.toLowerCase();
    return iconOptions.filter(
      (icon) =>
        icon.name.toLowerCase().includes(query) ||
        icon.keywords.some((keyword) => keyword.toLowerCase().includes(query)),
    );
  }, [searchQuery]);

  return (
    <div className="flex flex-col gap-4 px-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          type="text"
          placeholder="Search Icons"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 bg-primary-foreground border-grey-3"
        />
      </div>
      <div className="grid grid-cols-8 gap-2 max-h-[400px] overflow-y-auto p-2">
        {filteredIcons.map((iconOption) => (
          <button
            key={iconOption.name}
            onClick={() => onSelectIcon(iconOption)}
            className="p-2 hover:bg-grey-2 rounded-md transition-colors flex items-center justify-center"
            title={iconOption.name}
          >
            <FontAwesomeIcon icon={iconOption.icon} className="h-4 w-4 text-gray-600" />
          </button>
        ))}
      </div>
    </div>
  );
};

function getBase64(file: File): Promise<string | ArrayBuffer> {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  return new Promise((resolve) => {
    reader.addEventListener('load', () => resolve(reader.result));
  });
}

interface ImageIconSelectorProps {
  imageUrl: string | null;
  iconName: string | null;
  onImageChange: (file: File | null) => void;
  onImageUrlChange: (url: string) => void;
  onIconChange: (iconName: string | null) => void;
}

export const ImageIconSelector: React.FC<ImageIconSelectorProps> = ({
  imageUrl,
  iconName,
  onImageChange,
  onImageUrlChange,
  onIconChange,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [uploadType, setUploadType] = useState<'image' | 'icon'>('image');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(imageUrl);

  // Sync local state with props when they change
  React.useEffect(() => {
    setLocalImageUrl(imageUrl);
  }, [imageUrl]);

  // Helper function to get icon option from name
  const getIconOptionByName = (name: string | null): IconOption | undefined => {
    if (!name) return undefined;
    return iconOptions.find((opt) => opt.name === name);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    try {
      const file = event.target.files[0];
      const tmpImageUrl = await getBase64(file);

      // Update state in the correct order
      onImageUrlChange(tmpImageUrl as string);
      onImageChange(file);
      onIconChange(null); // Clear icon when image is uploaded

      // Update local state
      setLocalImageUrl(tmpImageUrl as string);

      setIsPopoverOpen(false);
    } catch (error) {
      // Silently handle errors
      console.error('Error uploading file:', error);
    }
  };

  const handleIconSelect = (icon: IconOption) => {
    // Clear image-related state
    onImageChange(null);
    onImageUrlChange('');
    setLocalImageUrl(null);

    // Set icon
    onIconChange(icon.name);
    setIsPopoverOpen(false);
  };

  const clearImage = () => {
    // Update state
    onImageChange(null);
    onImageUrlChange('');
    onIconChange(null);

    // Update local state
    setLocalImageUrl(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const renderUploadContent = () => {
    if (uploadType === 'icon') {
      return <IconSelector onSelectIcon={handleIconSelect} />;
    }

    const fileInfo = ['Supported File Format: PNG, JPEG, JPG', 'Maximum File Size: 2 MB'];

    if (localImageUrl) {
      return (
        <div className="flex flex-col items-start gap-2 p-4">
          <Card className="w-full bg-gray-50 border-dashed border-gray-200">
            <CardContent className="flex flex-col items-center justify-center gap-4 p-4">
              <div className="relative w-full rounded-lg overflow-hidden flex flex-col">
                <img
                  src={localImageUrl}
                  alt="Selected campaign"
                  className="w-[120px] h-[120px] object-cover rounded-lg m-auto"
                />
                <Button
                  variant="link"
                  className="hover:bg-white/100 text-red-500 px-3 pt-2 text-sm rounded-md w-fit"
                  onClick={(e) => {
                    e.preventDefault();
                    clearImage();
                  }}
                >
                  Remove
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-start gap-2 p-4">
        <Card className="w-full bg-gray-50 border-dashed border-gray-200">
          <CardContent className="flex flex-col items-center justify-center gap-4 px-4 py-12">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept="image/png, image/jpeg"
            />
            <Button
              variant="outline"
              className="h-8 px-4 py-[5px] bg-white border-gray-200 text-gray-700 font-medium"
              type="button"
              onClick={handleUploadButtonClick}
            >
              <PlusIcon className="w-3 h-3 mr-2" />
              Upload Image
            </Button>
            <div className="flex flex-col items-center w-full">
              {fileInfo.map((info, index) => (
                <p key={index} className="text-gray-400 text-xs text-center">
                  {info}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderImageContent = () => {
    const selectedIcon = getIconOptionByName(iconName);

    if (selectedIcon) {
      return (
        <div className="w-[224px] h-[148px] flex items-center justify-center rounded-xl bg-blue-400 text-white overflow-hidden">
          <FontAwesomeIcon icon={selectedIcon.icon} className="h-8 w-8" data-testid="font-awesome-icon" />
        </div>
      );
    }

    if (localImageUrl) {
      return (
        <Avatar className="w-[224px] h-[148px] rounded-xl overflow-hidden">
          <AvatarImage src={localImageUrl} className="object-cover w-full h-full rounded-xl" data-testid="avatar-image" />
        </Avatar>
      );
    }

    return (
      <div className="w-[224px] h-[148px] flex items-center justify-center rounded-xl bg-blue-400 text-white overflow-hidden">
        <FontAwesomeIcon icon={faTags} className="h-8 w-8" data-testid="font-awesome-icon" />
      </div>
    );
  };

  return (
    <div data-testid="image-icon-selector">
      <label className="font-medium text-grey-6 text-xs mb-2 flex items-center gap-2">
        <span>Image (optional)</span>
        <HoverCard>
          <HoverCardTrigger asChild>
            <FontAwesomeIcon icon={faCircleInfo} className="text-grey-3 cursor-pointer" data-testid="info-icon" />
          </HoverCardTrigger>
          <HoverCardContent className="w-80">
            <div className="flex flex-col gap-1">
              <h4 className="font-medium">Image Requirements</h4>
              <p className="text-sm text-muted-foreground">
                You can choose to upload a .png or .jpg file only (8MB max file size).
              </p>
            </div>
          </HoverCardContent>
        </HoverCard>
      </label>

      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <div className="relative flex items-center justify-center border-2 border-dashed border-[#E5E7EB] rounded-xl cursor-pointer w-[224px] h-[148px] bg-blue-400/0 p-0 overflow-hidden" data-testid="selector-container">
            {renderImageContent()}
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.preventDefault(); setIsPopoverOpen(true); }}
              onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') { setIsPopoverOpen(true); } }}
              className="absolute left-1/2 -translate-x-1/2 bottom-4 text-white underline cursor-pointer select-none text-sm font-medium z-10"
              data-testid="change-button"
            >
              Change
            </span>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0 rounded-xl" data-testid="popover-content">
          <header className="flex items-center justify-end gap-2.5 px-4 py-2">
            <div className="flex flex-col items-center gap-2.5 flex-1 grow rounded-xl">
              <ToggleGroup
                type="single"
                value={uploadType}
                onValueChange={(value) => setUploadType(value as 'image' | 'icon')}
                className="h-8 w-full bg-gray-100 rounded-lg flex items-center px-2 py-1"
                data-testid="upload-type-toggle"
              >
                <ToggleGroupItem
                  value="icon"
                  className="flex-1 h-full rounded-none py-1 data-[state=off]:bg-transparent data-[state=on]:bg-white data-[state=on]:rounded-lg"
                  data-testid="icon-toggle"
                >
                  <span className="font-medium text-xs text-gray-700">Icon</span>
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="image"
                  className="flex-1 h-full rounded-none py-1 data-[state=off]:bg-transparent data-[state=on]:bg-white data-[state=on]:rounded-lg"
                  data-testid="image-toggle"
                >
                  <span className="font-medium text-xs text-gray-700">Image</span>
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => setIsPopoverOpen(false)}
              className="cursor-pointer hover:border-gray-500 hover:text-gray-500 h-8 w-8 p-0"
              data-testid="close-button"
            >
              <XmarkIcon className="h-4 w-4" />
            </Button>
          </header>
          {renderUploadContent()}
        </PopoverContent>
      </Popover>
    </div>
  );
};
